import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import React, { ReactElement, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDashboardContext } from "../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { ORDER_DETAILS, ORDERS } from "../../constants/routes";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useNotificationMarkAllAsRead } from "../../hooks/api/v2/notification/useNotificationMarkAllAsRead";
import { useApiMutationOnView } from "../../hooks/api/v2/useApiMutationOnView";
import { useActiveTab } from "../../hooks/useActiveTab";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useParentNavigation } from "../../hooks/useParentNavigation";
import { AcceptedOrder } from "./AcceptedOrders/AcceptedOrder";
import { SupplierRequestedOrderPage } from "./RequestedOrder/SupplierRequestedOrderPage";

export function Order(): ReactElement {
  const orderId = useParamAsNumber(ORDER_DETAILS, "orderId");
  const location = useLocation();
  const navigateToParent = useParentNavigation({
    fallback: `${ORDERS}${location.search}`,
  });
  const { business } = useSellarCurrentBusiness();
  const { isTwoPanel } = useDashboardContext();
  const [showPaymentLinkPreview, setShowPaymentLinkPreview] = useState(false);
  const { mutate: markNotificationsAsRead } = useNotificationMarkAllAsRead();
  useApiMutationOnView({
    mutate: markNotificationsAsRead,
    variables: { orderId },
    enabled: !!orderId,
    requireBusinessMembership: true,
  });

  useEffect(() => {
    return () => {
      document.title = "Sellar Dashboard";
    };
  }, []);

  const onClickLeftIcon =
    isTwoPanel && !showPaymentLinkPreview
      ? undefined
      : () => {
          if (showPaymentLinkPreview) {
            return setShowPaymentLinkPreview(false);
          }

          return navigateToParent();
        };
  return (
    <>
      <NavigationBar
        title={
          showPaymentLinkPreview ? "Payment link preview" : "Order Details"
        }
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={onClickLeftIcon}
      />

      {business?.Supplier ? (
        <SupplierOrderDetails
          orderId={orderId}
          showPaymentLinkPreview={showPaymentLinkPreview}
          setShowPaymentLinkPreview={setShowPaymentLinkPreview}
        />
      ) : business?.Retailer ? (
        <BuyerOrderDetails orderId={orderId} />
      ) : null}
    </>
  );
}

const SupplierOrderDetails = ({
  orderId,
  showPaymentLinkPreview,
  setShowPaymentLinkPreview,
}: {
  orderId: number;
  showPaymentLinkPreview: boolean;
  setShowPaymentLinkPreview: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { activeTabName } = useActiveTab();
  useOrderDocumentTitle(orderId, getBuyerName);

  return activeTabName === "Order requests" ? (
    <SupplierRequestedOrderPage
      orderId={orderId}
      showPaymentLinkPreview={showPaymentLinkPreview}
      setShowPaymentLinkPreview={setShowPaymentLinkPreview}
    />
  ) : (
    <AcceptedOrder orderId={orderId} />
  );
};

const BuyerOrderDetails = ({ orderId }: { orderId: number }) => {
  useOrderDocumentTitle(orderId, getSupplierName);

  return <AcceptedOrder orderId={orderId} />;
};

const useOrderDocumentTitle = (
  orderId: number,
  getCounterpartyName: (order: OrderGetResponseType) => string | undefined,
) => {
  const navigate = useNavigate();
  const orderQuery = useOrder(orderId, {
    onError: (error) => {
      if (error.response?.status === 404) return navigate(ORDERS);
    },
  });

  useEffect(() => {
    if (!orderQuery.data) return;

    const counterpartyName = getCounterpartyName(orderQuery.data);
    document.title = `${generateOrderNumber(orderQuery.data)}${
      counterpartyName ? ` - ${counterpartyName}` : ""
    }`;
  }, [getCounterpartyName, orderQuery.data]);
};

const getBuyerName = (order: OrderGetResponseType) =>
  order.Retailer.Business.name;

const getSupplierName = (order: OrderGetResponseType) =>
  order.Supplier.Business?.name;

const generateOrderNumber = (o?: { id: number }) => {
  if (!o) return "";

  return `Order #${o.id}`;
};
